// @ts-check
import React from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core'; // eslint-disable-line no-unused-vars
import { useField, ErrorMessage } from 'formik';
import {
  VerticalField,
  HorizontalField,
} from 'preferral-ui/components/FieldStructure';
import Spinner from 'shared/components/Spinner';
import { FAIcon } from 'preferral-ui/components/FAIcon';
import { IconContainer } from '../IconContainer';
import { SpinnerContainer } from '../SpinnerContainer';

/** @typedef {{
 value?: string;
  icon?: IconProp;
  iconRightContent?: React.ReactNode;
  isLoading?: boolean;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  containerClassName?: string;
  className?: string;
  onChange?(newValue: string): void;
  onBlur?(e: any): void;
  onFocus?(e: any): void;
}} InputTextProps */

/** @type { React.FC<InputTextProps> } */
export const InputText = (props) => {
  const {
    value,
    onChange,
    onBlur,
    onFocus,
    icon,
    isLoading = false,
    inputProps = {},
    containerClassName = '',
    className = '',
    iconRightContent,
  } = props;

  /**
   *
   * @param {React.ChangeEvent<HTMLInputElement>} evt
   */
  function handleChange(evt) {
    if (onChange) onChange(evt.target.value);
  }

  /**
   * NB: `type` and `className` can both be overridden by `inputProps`.
   */

  /* eslint-disable max-len */
  return (
    <div
      className={`rounded-md shadow-sm relative ${
        !!icon ? 'relative' : ''
      } ${containerClassName}`}
    >
      <input
        type="text"
        value={value || ''}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        className={`appearance-none block h-auto w-full m-0 px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 ${className} ${
          !!icon ? 'pl-10' : ''
        }`}
        {...inputProps}
      />
      {icon && (
        <IconContainer className="text-gray-500">
          <FAIcon icon={icon} />
        </IconContainer>
      )}
      {isLoading ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : iconRightContent ? (
        <SpinnerContainer>{iconRightContent}</SpinnerContainer>
      ) : null}
    </div>
  );
  /* eslint-enable max-len */
};

/**
 * TextInput.
 */

/** @typedef {{
  name: string;
  id?: string;
  placeholder?: string;
  autoFocus?: boolean;
  icon?: IconProp;
  type?: string;
  className?: string;
  isLoading?: boolean;
  inputProps?: React.HTMLAttributes<HTMLInputElement>;
}} TextInputProps */

/** @type { React.FC<TextInputProps> } */
export const TextInput = (props) => {
  const {
    id,
    name,
    placeholder,
    icon,
    type = 'text',
    autoFocus = false,
    isLoading = false,
    className = '',
    inputProps = {},
  } = props;

  const [field, meta, helpers] = useField(name);
  const { value = '', onBlur } = field;
  const { setValue } = helpers;

  const isMobile = /iPad|iPhone|iPod/g.test(navigator.userAgent);

  return (
    <>
      <InputText
        inputProps={{
          id: id || name,
          type,
          placeholder,
          name,
          autoFocus: autoFocus && !isMobile,
          ...inputProps,
        }}
        icon={icon}
        className={
          meta && meta.touched && meta.error
            ? `${className} border border-red-500`
            : className
        }
        value={value}
        onChange={setValue}
        onBlur={onBlur}
        isLoading={isLoading}
      />
      <ErrorMessage
        component="p"
        name={name}
        className="mt-2 text-xs italic text-red-500"
      />
    </>
  );
};

/**
 * TextField.
 */

/** @typedef {{
  label: string;
  indicateOptional?: boolean;
} & TextInputProps} TextFieldProps */

/** @type { React.FC<TextFieldProps> } */
export const TextField = (props) => {
  const { label, indicateOptional, ...rest } = props;

  return (
    <VerticalField
      id={`field--${rest.id || rest.name}`}
      htmlFor={rest.id || rest.name}
      label={label}
      indicateOptional={indicateOptional}
    >
      <TextInput {...props} placeholder={props.placeholder || label} />
    </VerticalField>
  );
};

/** @type { React.FC<TextFieldProps> } */
export const HorizontalTextField = (props) => {
  const { label, indicateOptional, ...rest } = props;

  return (
    <HorizontalField
      id={`field--${rest.id || rest.name}`}
      htmlFor={rest.id || rest.name}
      label={label}
      indicateOptional={indicateOptional}
    >
      <TextInput {...rest} placeholder={rest.placeholder || label} />
    </HorizontalField>
  );
};
